//@flow
import React, {useEffect, useRef, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {
  Paper,
  makeStyles,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import {Alert} from '@material-ui/lab';
import {ArrowBack} from '@material-ui/icons';
import {FormProvider, useForm} from 'react-hook-form';

import {colors} from '../../../../../assets/styles/colors';
import FormSection from './FormSection';
import IdSection from './Sections/IdSection';
import FiltersSection from './Sections/Filters';
import ActionSection from './Sections/ActionSection';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {useChatTriggerActions} from '../../utils';
import InlineLoader from '../../../../../elements/LoadingSpinner/InlineLoader';
import type {TriggerFields} from './const';
import Toast from '../../../../../elements/Toast';
import {useIsFeatureInTier} from '../../../../../utils/component';
import TierRestrictionModal from '../../../../../elements/TierRestrictionModal/TierRestrictionModal';
import {
  getDefaultFormValues,
  normalizeTriggerToFormValues,
} from '../../utils/adapters';
import {
  DEFAULT_TRIGGER_TYPE,
  getActionTypeForTriggerType,
  isCustomerInactivityTrigger,
} from '../../utils/triggerConfig';
import {validateTriggerForm} from '../../utils/validation';
import {
  applyValidationErrors,
  collectValidationMessages,
} from '../../utils/formErrors';
import ValidationAlert from './ValidationAlert';
import {getTriggerService} from '../../promises';
import TriggerExecutionHistory from '../TriggerExecutionHistory';
import TriggerSimulateEstimate from '../TriggerSimulateEstimate';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: 'calc(100vh - 100px)',
    padding: '.25rem',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    [theme.breakpoints.up('md')]: {
      paddingTop: '1rem',
    },
  },
  container: {
    width: '100%',
    height: '100%',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);',
    borderRadius: '12px',
    maxWidth: '800px',
    padding: '1rem',
    position: 'relative',
  },
  contentGrid: {
    display: 'grid',
    gap: '1rem',
  },
  leftContent: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    width: '100%',
    marginBottom: '1rem',
  },
  title: {
    fontWeight: '600',
    fontSize: '1rem',
  },
  form: {
    boxShadow: 'inset 0px 0px 10px rgba(0, 0, 0, 0.06);',
    padding: '1rem',
    background: colors.grey100,
    borderRadius: '12px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: '#ffffffa3',
    zIndex: 1,
  },
  submitButtonStickyWrapper: {
    position: 'sticky',
    bottom: '64px',
    width: '100%',
    zIndex: 1,
    background: 'white',
    padding: '1rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      bottom: '0',
    },
  },
  submitButtonSticky: {
    width: '100%',
    maxWidth: '400px',
  },
  submitButtonLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '.5rem',
  },
}));

export default function TriggerForm() {
  const classes = useStyles();
  const history = useHistory();
  const {triggerId} = useParams();

  const hasTriggerFeature = useIsFeatureInTier('TIENDACHAT_TRIGGERS');
  const [tierModalOpen, setTierModalOpen] = useState(false);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const [formLoadKey, setFormLoadKey] = useState(0);
  const validationAlertRef = useRef<?HTMLElement>(null);
  const isSubmittingRef = useRef(false);

  const isEdit = triggerId !== 'new';
  const methods = useForm<TriggerFields>({
    defaultValues: getDefaultFormValues(),
  });

  const {
    setError,
    clearErrors,
    getValues,
    reset,
    errors: formErrors,
    watch,
  } = methods;

  const watchedTriggerType = watch('triggerType');
  const showInactivityEstimate =
    isEdit && isCustomerInactivityTrigger(watchedTriggerType);

  useEffect(() => {
    if (!isEdit || !triggerId) return;

    getTriggerService(triggerId)
      .then(trigger => {
        reset(normalizeTriggerToFormValues(trigger));
        setFormLoadKey(current => current + 1);
      })
      .catch(() => {
        // Fallback handled by empty form defaults
      });
  }, [isEdit, reset, triggerId]);

  const {
    createTrigger,
    loading,
    editTrigger,
    error,
    errorMessage,
    clearError,
  } = useChatTriggerActions();

  const handleSubmitSuccess = (state?: any) => {
    history.push('/chat/triggers', state);
  };

  const getActionSnapshot = (values: TriggerFields = getValues()) => ({
    triggerType: values.triggerType,
    actionType: values.actionType,
    provider: values.provider,
    deviceId: values.deviceId,
    response: values.response,
    templateName: values.templateName,
    executionTimeSlot: values.executionTimeSlot,
    filters: values.filters,
  });

  const showValidationErrors = (
    customErrors: {[key: string]: string} = {},
    meta: {
      path?: string,
      validationValid?: boolean,
      rhfErrors?: any,
    } = {}
  ) => {
    applyValidationErrors(setError, customErrors);

    const messages = collectValidationMessages(customErrors);
    const actionSnapshot = getActionSnapshot();
    const path = meta.path || 'unknown';

    // Debug: surface exact failing fields (generic toast hides them).
    // eslint-disable-next-line no-console
    console.warn('[TriggerForm] validation failed', {
      path,
      validationValid: meta.validationValid,
      customErrors,
      customErrorKeys: Object.keys(customErrors || {}),
      customErrorEntries: Object.entries(customErrors || {}),
      collectedMessages: messages,
      actionSnapshot,
      rhfErrors: meta.rhfErrors,
      formErrors,
    });

    if (!messages.length) {
      // eslint-disable-next-line no-console
      console.warn(
        '[TriggerForm] falling back to generic validation message — customErrors was empty',
        {
          path,
          validationValid: meta.validationValid,
          customErrors,
          rhfErrors: meta.rhfErrors,
          formErrors,
          actionSnapshot,
          reason:
            'collectValidationMessages returned [] because customErrors had no string values',
        }
      );
    }

    setValidationMessages(
      messages.length
        ? messages
        : ['Completa los campos requeridos del formulario']
    );

    requestAnimationFrame(() => {
      if (validationAlertRef.current) {
        validationAlertRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    });
  };

  const handleSubmit = async (data: TriggerFields) => {
    if (isSubmittingRef.current || loading) return;

    if (!hasTriggerFeature) {
      setTierModalOpen(true);
      return;
    }

    const validation = validateTriggerForm(data);
    if (!validation.valid) {
      showValidationErrors(validation.errors, {
        path: 'validateTriggerForm',
        validationValid: false,
      });
      return;
    }

    clearErrors();
    setValidationMessages([]);

    const payload = {
      ...data,
      actionType: getActionTypeForTriggerType(
        data.triggerType || DEFAULT_TRIGGER_TYPE
      ),
      action: {
        actionType: getActionTypeForTriggerType(
          data.triggerType || DEFAULT_TRIGGER_TYPE
        ),
        text: data.response,
        attachments: data.attachments || [],
        documents: data.savedDocuments || [],
        images: data.savedImages || [],
        videos: data.savedVideos || [],
        provider: data.provider,
        deviceId: data.deviceId,
        templateName: data.templateName,
        templateLang: data.templateLang,
        templateComponents: data.templateComponents || [],
      },
      enabled: true,
    };

    isSubmittingRef.current = true;

    try {
      const trigger =
        isEdit && triggerId
          ? await editTrigger(triggerId, payload)
          : await createTrigger(payload);

      if (!trigger) return;

      handleSubmitSuccess(isEdit ? {edited: triggerId} : {added: true});
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleSaveClick = () => {
    if (isSubmittingRef.current || loading) return;

    // Prefer full getValues() so nested filter paths are available; parent
    // `filters` is kept current via setFilterFieldUpdates (RMW).
    const values = getValues();
    const validation = validateTriggerForm(values);

    // eslint-disable-next-line no-console
    console.warn('[TriggerForm] save click', {
      path: 'validateTriggerForm',
      validationValid: validation.valid,
      customErrors: validation.errors,
      filters: values.filters,
      actionSnapshot: getActionSnapshot(values),
    });

    if (!validation.valid) {
      showValidationErrors(validation.errors, {
        path: 'validateTriggerForm',
        validationValid: false,
      });
      return;
    }

    // Drive save via validateTriggerForm — skip RHF required traps on
    // setValue/reset fields that produced empty customErrors + generic toast.
    handleSubmit(values);
  };

  return (
    <FormProvider {...methods}>
      <Toast severity="error" color="error" open={error} onClose={clearError}>
        {errorMessage || 'Ocurrió un error al guardar el Trigger'}
      </Toast>
      <div className={classes.root}>
        <Alert severity="info">
          Las respuestas automatizadas configuradas solo funcionarán cuando el
          chat esté cerrado.
        </Alert>
        <Paper className={classes.container}>
          <InlineLoader className={classes.loader} loading={loading} />
          <header className={classes.header}>
            <CustomButton to="/chat/triggers/">
              <ArrowBack />
            </CustomButton>
            <Typography className={classes.title}>
              {isEdit
                ? 'Editar respuesta automatizada'
                : 'Nueva respuesta automatizada'}
            </Typography>
          </header>

          <div className={classes.contentGrid}>
            <div className={classes.leftContent}>
              <ValidationAlert
                ref={validationAlertRef}
                messages={validationMessages}
                onClose={() => setValidationMessages([])}
              />
              <form className={classes.form}>
                <FormSection
                  title="1. Identificador respuesta"
                  subtitle="Debe ser minúsculas, sin espacios"
                >
                  <IdSection />
                </FormSection>
                <FormSection
                  title="2. Definir regla/condicional"
                  badgeLabel="DISPARADOR"
                  subtitle="Configura el tipo de disparador y sus filtros"
                >
                  <FiltersSection
                    key={formLoadKey}
                    canChangeTriggerType={!isEdit}
                  />
                </FormSection>
                <FormSection
                  title="3. Definir mensaje de respuesta"
                  badgeLabel="RESPUESTA"
                  subtitle="Se enviará el siguiente mensaje según el tipo de disparador"
                >
                  <ActionSection key={`action-${formLoadKey}`} />
                </FormSection>
                {isEdit && (
                  <FormSection
                    title="4. Conversaciones ejecutadas"
                    badgeLabel="HISTORIAL"
                    subtitle="Conversaciones en las que este trigger ya se ejecutó"
                  >
                    {showInactivityEstimate && (
                      <TriggerSimulateEstimate
                        triggerId={triggerId}
                        enabled={showInactivityEstimate}
                      />
                    )}
                    <TriggerExecutionHistory
                      triggerId={triggerId}
                      enabled={isEdit}
                    />
                  </FormSection>
                )}
              </form>
            </div>
          </div>
          <div className={classes.submitButtonStickyWrapper}>
            <CustomButton
              onClick={handleSaveClick}
              variant="contained"
              className={classes.submitButtonSticky}
              disabled={loading}
            >
              <span className={classes.submitButtonLabel}>
                {loading && <CircularProgress size={18} color="inherit" />}
                <span>{loading ? 'Guardando...' : 'Guardar'}</span>
              </span>
            </CustomButton>
          </div>
        </Paper>
      </div>
      <TierRestrictionModal
        open={tierModalOpen && !hasTriggerFeature}
        handleClose={() => {
          setTierModalOpen(false);
        }}
      />
    </FormProvider>
  );
}
